
// ABOUTME: Authentication hook for managing user session and profile data
// ABOUTME: Provides login, logout, and user profile state management
import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import * as Sentry from "@sentry/react";
import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';
import { traceDBOperation, captureError, logger } from '@/utils/sentry';

interface AuthContextType {
  user: User | null;
  profile: any | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
        // Set user context in Sentry
        Sentry.setUser({
          id: session.user.id,
          email: session.user.email,
        });
        logger.info("User session restored", { userId: session.user.id });
      }
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        logger.debug(logger.fmt`Auth state changed: ${event}`, { 
          hasSession: !!session,
          userId: session?.user?.id 
        });

        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          fetchProfile(session.user.id);
          // Set user context in Sentry
          Sentry.setUser({
            id: session.user.id,
            email: session.user.email,
          });
        } else {
          setProfile(null);
          // Clear user context in Sentry
          Sentry.setUser(null);
        }
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId: string) => {
    try {
      const data = await traceDBOperation(
        'SELECT',
        'profiles',
        async () => {
          const { data, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', userId)
            .single();
          
          if (error) throw error;
          return data;
        },
        { userId }
      );
      
      setProfile(data);
      
      // Add profile data to Sentry user context
      Sentry.setUser({
        id: userId,
        email: data.email,
        username: `${data.first_name} ${data.last_name}`.trim(),
        first_name: data.first_name,
        last_name: data.last_name,
      });

      logger.info("Profile fetched successfully", { 
        userId,
        hasProfile: !!data 
      });
    } catch (error) {
      logger.error("Error fetching profile", { 
        userId,
        error: error.message 
      });
      captureError(error as Error, { 
        operation: 'fetchProfile',
        userId 
      });
    }
  };

  const signOut = async () => {
    try {
      await traceDBOperation(
        'SIGN_OUT',
        'auth',
        async () => {
          const { error } = await supabase.auth.signOut();
          if (error) throw error;
        }
      );
      
      // Clear user context in Sentry
      Sentry.setUser(null);
      logger.info("User signed out successfully");
    } catch (error) {
      logger.error("Error signing out", { error: error.message });
      captureError(error as Error, { operation: 'signOut' });
    }
  };

  return (
    <AuthContext.Provider value={{ user, profile, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
